// /admin/v1/revenue (contracts/revenue.md §4): the overview, customers (subscriptions), events and one player's
// subscriptions and journey. Lenient like paymentsSchemas: counts default to 0, lists to [], nullish and "" read
// as null, ids may be numbers, currencies are upper-cased and any environment but production reads as sandbox,
// so a backend that sends a little less still parses.

import { z } from "zod";
import { DEFAULT_ESTIMATES, type RevenueEnv } from "../lib/revenue";

/** A string that may be missing, null or ""; all read as null. */
const text = z
  .string()
  .nullish()
  .transform((v) => (v ? v : null));
/** An id the API may send as a number. */
const id = z
  .union([z.string(), z.number()])
  .nullish()
  .transform((v) => (v === null || v === undefined || v === "" ? null : String(v)));
const count = z
  .number()
  .nullish()
  .transform((v) => v ?? 0);
const numOrNull = z
  .number()
  .nullish()
  .transform((v) => v ?? null);
const flag = (fallback: boolean) =>
  z
    .boolean()
    .nullish()
    .transform((v) => v ?? fallback);

export const Money = z.object({ amount: z.number(), currency: z.string().transform((c) => c.toUpperCase()) });
export type Money = z.infer<typeof Money>;
const money = Money.nullish().transform((v) => v ?? null);
const moneyList = z
  .array(Money)
  .nullish()
  .transform((v) => v ?? []);

/** `production`, else sandbox (App Store Sandbox and Xcode, Stripe test mode); missing reads as production. */
const environment = z
  .string()
  .nullish()
  .transform((v): RevenueEnv => (!v || v.toLowerCase() === "production" ? "production" : "sandbox"));
const store = z
  .string()
  .nullish()
  .transform((v) => (v ? v.toLowerCase() : "appstore"));

// ---------------------------------------------------------------- subscriptions and events

export const Attribution = z.object({ trigger: text, offer: text, experimentId: id, variant: text });
export type Attribution = z.infer<typeof Attribution>;

export const NextCharge = z.object({ at: z.string(), price: money, usd: numOrNull });
export type NextCharge = z.infer<typeof NextCharge>;

export const Subscription = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  store,
  environment,
  userId: text,
  handle: text,
  country: text,
  storefront: text,
  productId: text,
  plan: text,
  status: z.string(),
  endReason: text,
  startedAt: text,
  trialEndsAt: text,
  currentPeriodEndsAt: text,
  graceEndsAt: text,
  willRenew: flag(false),
  cancelledAt: text,
  converted: z
    .boolean()
    .nullish()
    .transform((v) => v ?? null),
  nextCharge: NextCharge.nullish().transform((v) => v ?? null),
  paidPeriods: count,
  spent: moneyList,
  spentUsd: count,
  attribution: Attribution.nullish().transform((v) => v ?? null),
  lastEvent: z
    .object({ kind: z.string(), at: z.string() })
    .nullish()
    .transform((v) => v ?? null),
  externalId: text,
  stripeUrl: text,
});
export type Subscription = z.infer<typeof Subscription>;

export const RevenueEvent = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  at: z.string(),
  kind: z.string(),
  detail: text,
  store,
  environment,
  raw: text,
  userId: text,
  handle: text,
  subscriptionId: id,
  productId: text,
  plan: text,
  storefront: text,
  price: money,
  usd: numOrNull,
  periodEndsAt: text,
});
export type RevenueEvent = z.infer<typeof RevenueEvent>;

export const Customers = z
  .object({
    items: z.array(Subscription),
    total: numOrNull,
    counts: z
      .record(z.string(), z.number())
      .nullish()
      .transform((v) => v ?? {}),
    nextCursor: text,
  })
  .transform((c) => ({ ...c, total: c.total ?? c.items.length }));
export type Customers = z.infer<typeof Customers>;

export const RevenueEvents = z.object({ items: z.array(RevenueEvent), nextCursor: text });
export type RevenueEvents = z.infer<typeof RevenueEvents>;

// ---------------------------------------------------------------- overview

const Estimates = z.object({
  appleCommission: z.number().default(DEFAULT_ESTIMATES.appleCommission),
  stripeFeePercent: z.number().default(DEFAULT_ESTIMATES.stripeFeePercent),
  stripeFeeFixedUsd: z.number().default(DEFAULT_ESTIMATES.stripeFeeFixedUsd),
});

const Now = z
  .object({
    at: text,
    activeSubscriptions: count,
    activeByPlan: z
      .record(z.string(), z.number())
      .nullish()
      .transform((v) => v ?? {}),
    activeTrials: count,
    trialsWillConvert: count,
    trialsCancelled: count,
    cancelledActive: count,
    lifetime: count,
    billingIssues: count,
    mrrUsd: count,
    arrUsd: numOrNull,
    customers: count,
    payingCustomers: count,
  })
  .transform((n) => ({ ...n, arrUsd: n.arrUsd ?? n.mrrUsd * 12 }));

export const Upcoming = z.object({
  subscriptionId: id,
  userId: text,
  handle: text,
  at: z.string(),
  /** `conversion` (a trial's first charge) or `renewal`. */
  kind: z
    .string()
    .nullish()
    .transform((v) => v ?? "renewal"),
  productId: text,
  plan: text,
  price: money,
  usd: numOrNull,
  store: z
    .string()
    .nullish()
    .transform((v) => (v ? v.toLowerCase() : null)),
  environment: z
    .string()
    .nullish()
    .transform((v): RevenueEnv | null => (!v ? null : v.toLowerCase() === "production" ? "production" : "sandbox")),
});
export type Upcoming = z.infer<typeof Upcoming>;

const Totals = z.object({
  revenueUsd: count,
  proceedsUsd: count,
  revenue: moneyList,
  transactions: count,
  refunds: count,
  refundedUsd: count,
  newTrials: count,
  conversions: count,
  trialCancellations: count,
  newPaid: count,
  renewals: count,
  cancellations: count,
  reactivations: count,
  expirations: count,
  billingIssues: count,
  lifetimePurchases: count,
  newCustomers: count,
});

const TrialConversion = z.object({ started: count, converted: count, ended: count, pending: count, rate: numOrNull });

export const RevenueDay = z.object({
  date: z.string(),
  revenueUsd: count,
  proceedsUsd: count,
  transactions: count,
  newTrials: count,
  conversions: count,
  newPaid: count,
  renewals: count,
  cancellations: count,
  expirations: count,
  refunds: count,
  activeSubscriptions: count,
  activeTrials: count,
  mrrUsd: count,
});
export type RevenueDay = z.infer<typeof RevenueDay>;

const list = <T extends z.ZodType>(item: T) =>
  z
    .array(item)
    .nullish()
    .transform((v) => (v ?? []) as z.output<T>[]);

export const RevenueOverview = z.object({
  from: z.string(),
  to: z.string(),
  env: z
    .string()
    .nullish()
    .transform((v) => v ?? "production"),
  store: z
    .string()
    .nullish()
    .transform((v) => v ?? "all"),
  estimates: Estimates.nullish().transform((v) => v ?? { ...DEFAULT_ESTIMATES }),
  now: Now,
  upcoming: list(Upcoming),
  totals: Totals.nullish().transform((v) => v ?? Totals.parse({})),
  trialConversion: TrialConversion.nullish().transform((v) => v ?? TrialConversion.parse({})),
  days: list(RevenueDay),
  byProduct: list(z.object({ productId: text, plan: text, revenueUsd: count, transactions: count, trials: count, active: count })),
  byStorefront: list(z.object({ storefront: text, revenueUsd: count, transactions: count, customers: count })),
  byTrigger: list(z.object({ trigger: text, starts: count, trials: count, revenueUsd: count })),
  funnel: z
    .object({ paywallPlayers: count, trialStarters: count, paidStarters: count, converted: count })
    .nullish()
    .transform((v) => v ?? null),
});
export type RevenueOverview = z.infer<typeof RevenueOverview>;

// ---------------------------------------------------------------- one player

export const Grant = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  createdAt: text,
  expiresAt: text,
  revokedAt: text,
  reason: text,
  grantedBy: text,
});
export type Grant = z.infer<typeof Grant>;

export const JourneyItem = z
  .object({
    at: z.string(),
    kind: z.string(),
    event: RevenueEvent.nullish().transform((v) => v ?? null),
    trigger: text,
    variant: text,
    subscriptionId: id,
    price: money,
    usd: numOrNull,
    /** Grant steps: the reason, end and who granted it, flat or as `grant`. */
    reason: text,
    expiresAt: text,
    grantedBy: text,
    grant: Grant.partial().nullish(),
  })
  .transform(({ grant, ...s }) => ({
    ...s,
    reason: s.reason ?? grant?.reason ?? null,
    expiresAt: s.expiresAt ?? grant?.expiresAt ?? null,
    grantedBy: s.grantedBy ?? grant?.grantedBy ?? null,
  }));
export type JourneyItem = z.infer<typeof JourneyItem>;

export const PlayerRevenue = z.object({
  player: z.object({ userId: z.string(), handle: text, country: text, tz: text, createdAt: text, lastSeenAt: text }),
  summary: z
    .object({
      status: z
        .string()
        .nullish()
        .transform((v) => v ?? "free"),
      spent: moneyList,
      spentUsd: count,
      customerSince: text,
      paywallViews: count,
      firstPaywallAt: text,
    })
    .nullish()
    .transform((v) => v ?? { status: "free", spent: [], spentUsd: 0, customerSince: null, paywallViews: 0, firstPaywallAt: null }),
  subscriptions: list(Subscription),
  grants: list(Grant),
  journey: list(JourneyItem),
});
export type PlayerRevenue = z.infer<typeof PlayerRevenue>;
